/**
 * KPI Trends Hour segment 纯函数层。
 *
 * 两套「是否 Hour」判断（勿混用）：
 * - isKpiTrendsHourlyDateType(rangeTimeType) → Filter UI segment，`rangeTime.type === 'hour'`
 * - isKpiTrendsHourlyGranularity(dateType)   → API / fetchParams，`date_type === 'hourly'`
 * 映射：toDateTypeLy['hour'] === 'hourly'
 *
 * React 状态编排 → hooks/useKpiTrendsHourSegment.ts；API 请求回填 → hooks/useKPITrends.tsx
 *
 * Import 约定：Filter 配置 / 跨模块稳定 API → 从 const.tsx re-export 引入；
 * 页面内部逻辑 → 直接 import 本文件。
 */
import moment from 'moment';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import {
  clampHourRange,
  createDefaultDisabledDate,
  createHourShortcuts,
  DEFAULT_MAX_HOUR_RANGE_DAYS,
  formatHourDateTime,
  isValidHourRangeValue,
  normalizeHourRangeValue,
} from '@/pages/intelligence/components/RangeHourPicker/hourRange';
import type { THourRangeValue } from '@/pages/intelligence/components/RangeHourPicker/types';
import { toDateTypeLy } from '@/pages/intelligence/utils/common';
import { getSortedDates } from '@/pages/intelligence/utils/commonUtils';
import {
  getSourceMetricsByValue,
  getMetricOptionByValue,
} from './components/MetricsSelection/config';

/** Steam Hourly CCU 指标 key */
export const KPI_TRENDS_HOURLY_CCU_METRIC = 'source_steam__hourly_ccu';

export const KPI_TRENDS_HOURLY_SOURCE_METRICS = {
  source_steam: [KPI_TRENDS_HOURLY_CCU_METRIC],
};

/** Hour segment 仅保留 My Filter + 时间筛选器 + Save/Reset */
export const KPI_TRENDS_HOURLY_FILTER_KEY_LIST = [
  filterKeyMap.ROW,
  filterKeyMap.MY_FILTER,
  filterKeyMap.RANGE_TIME,
  filterKeyMap.SAVE_FILTER_SETTINGS,
  filterKeyMap.KPITRENDS_REASE,
];

export const MAX_HOUR_RANGE_DAYS = DEFAULT_MAX_HOUR_RANGE_DAYS;

const EMPTY_HOUR_RANGE: ['', ''] = ['', ''];

export const isKpiTrendsHourlyDateType = (rangeTimeType?: string): boolean =>
  Boolean(rangeTimeType && toDateTypeLy[rangeTimeType] === 'hourly');

export const isKpiTrendsHourlyGranularity = (dateType?: string): boolean =>
  dateType === 'hourly';

export const buildHourlyMetricSelectionValue = () => {
  const metrics = [KPI_TRENDS_HOURLY_CCU_METRIC];
  return {
    type: 'hourly',
    types: 'all',
    metrics,
    sources: ['source_steam'],
    source_metrics: getSourceMetricsByValue(metrics),
    metricsOptions: getMetricOptionByValue(metrics),
    metricsOptionsByConfigOrder: getMetricOptionByValue(metrics, true),
    selectedMetricsValue: 'metrics',
    shotSelectValue: 'all',
    selectCutVal: 'hot',
  };
};

export const getHourlyTableMetricOptions = () =>
  getMetricOptionByValue([KPI_TRENDS_HOURLY_CCU_METRIC], true);

/** Visual metric 选项：非 Hour 粒度下排除 hourly CCU */
export const filterVisMetricsOptionsForGranularity = (
  options: { value: string; }[],
  dateType?: string,
): { value: string; }[] =>
  options.filter(
    (option) =>
      isKpiTrendsHourlyGranularity(dateType) ||
      option.value !== KPI_TRENDS_HOURLY_CCU_METRIC,
  );

export const findFirstNonHourlyCcuMetric = (
  options: { value: string; }[],
): string | undefined =>
  options.find((option) => option.value !== KPI_TRENDS_HOURLY_CCU_METRIC)
    ?.value;

/** 01:00–23:59 边界 + 最长 7 天 */
export const clampKpiTrendsHourlyRange = clampHourRange;

/** API 入参：YYYY-MM-DD HH:mm:ss，秒位由 boundary 决定 */
export const formatKpiTrendsHourlyDateTime = formatHourDateTime;

const parseResponseHourRange = (
  startDate: string,
  endDate: string,
): [moment.Moment | string, moment.Moment | string] => {
  if (!startDate || !endDate) return EMPTY_HOUR_RANGE;
  return clampKpiTrendsHourlyRange(startDate, endDate);
};

/** 从 compare 响应 metric_value_list 收集有效 date */
export const collectHourlyMetricDates = (data: any[]): string[] => {
  const dates: string[] = [];
  for (const game of data || []) {
    for (const item of game?.metric_value_list || []) {
      if (item?.date && moment(item.date).isValid()) {
        dates.push(item.date);
      }
    }
  }
  return dates;
};

/**
 * Hour 首屏回填：优先用响应数据首尾 hour，与图表一致；无数据点再回退 req
 * 例：req 00:00~次日01:00，但 data 仅 01:00~15:00 → 回填 01:00~15:00
 */
export const resolveHourlyBackfillRange = (
  reqStart: string,
  reqEnd: string,
  data: any[],
): { startDate: string; endDate: string; } => {
  const dataDates = collectHourlyMetricDates(data).sort();
  if (dataDates.length) {
    const [clampedStart, clampedEnd] = clampKpiTrendsHourlyRange(
      dataDates[0],
      dataDates[dataDates.length - 1],
    );
    return {
      startDate: formatKpiTrendsHourlyDateTime(clampedStart, 'start'),
      endDate: formatKpiTrendsHourlyDateTime(clampedEnd, 'end'),
    };
  }
  if (reqStart && reqEnd) {
    const [clampedStart, clampedEnd] = clampKpiTrendsHourlyRange(reqStart, reqEnd);
    return {
      startDate: formatKpiTrendsHourlyDateTime(clampedStart, 'start'),
      endDate: formatKpiTrendsHourlyDateTime(clampedEnd, 'end'),
    };
  }
  return { startDate: '', endDate: '' };
};

/**
 * 切换至 Hour 时的 filter patch。
 * 无有效 range 时置空并由后端响应回填；My Filter / Segment 缓存已有有效 range 则保留。
 */
export const buildKpiTrendsHourFilterValuePatch = (prevRange?: unknown) => ({
  [filterKeyMap.RANGE_TIME]: {
    type: 'hour' as const,
    value: isValidHourRangeValue(prevRange as THourRangeValue)
      ? (prevRange as [moment.Moment, moment.Moment])
      : EMPTY_HOUR_RANGE,
  },
  [filterKeyMap.METRIC_SELECT_FOR_KPI_TREND]: {
    value: buildHourlyMetricSelectionValue(),
  },
});

/** Cascader value 必须是 plain object；saved filter 可能写入 undefined/null */
const resolveCascaderFilterEntry = (
  filterValue: Record<string, any>,
  defaultFilterValue: Record<string, any>,
  key: string,
) => {
  const raw = filterValue?.[key]?.value;
  const resolvedValue =
    raw !== null &&
    raw !== undefined &&
    typeof raw === 'object'
      ? raw
      : defaultFilterValue?.[key]?.value;
  return {
    ...defaultFilterValue?.[key],
    ...filterValue?.[key],
    value: resolvedValue,
  };
};

export const shouldRestoreDefaultMetricsWhenLeavingHour = (
  metrics?: string[],
): boolean =>
  !Array.isArray(metrics) ||
  !metrics.length ||
  (metrics.length === 1 && metrics[0] === KPI_TRENDS_HOURLY_CCU_METRIC);

export const buildFilterValuePatchWhenLeavingHour = (
  prev: Record<string, any>,
  defaultFilterValue: Record<string, any>,
): Record<string, any> => {
  const hourlyMetrics =
    prev?.[filterKeyMap.METRIC_SELECT_FOR_KPI_TREND]?.value?.metrics;
  const isStillHourlyMetrics =
    shouldRestoreDefaultMetricsWhenLeavingHour(hourlyMetrics);

  return {
    ...prev,
    [filterKeyMap.CASCADER]: resolveCascaderFilterEntry(
      prev,
      defaultFilterValue,
      filterKeyMap.CASCADER,
    ),
    [filterKeyMap.PLATFORMCASCADER]: resolveCascaderFilterEntry(
      prev,
      defaultFilterValue,
      filterKeyMap.PLATFORMCASCADER,
    ),
    ...(isStillHourlyMetrics
      ? {
        [filterKeyMap.METRIC_SELECT_FOR_KPI_TREND]:
          defaultFilterValue[filterKeyMap.METRIC_SELECT_FOR_KPI_TREND],
      }
      : {}),
  };
};

const patchCascaderFilterEntries = (
  filterValue: Record<string, any>,
  defaultFilterValue: Record<string, any>,
) => {
  const cascaderKey = filterKeyMap.CASCADER;
  const platformCascaderKey = filterKeyMap.PLATFORMCASCADER;
  return {
    [cascaderKey]: resolveCascaderFilterEntry(
      filterValue,
      defaultFilterValue,
      cascaderKey,
    ),
    [platformCascaderKey]: resolveCascaderFilterEntry(
      filterValue,
      defaultFilterValue,
      platformCascaderKey,
    ),
  };
};

/** Hour 模式：仅补齐 Cascader，不展开 defaultFilterValue */
export const patchFilterValueCascaderDefaults = (
  filterValue: Record<string, any>,
  defaultFilterValue: Record<string, any>,
): Record<string, any> => ({
  ...filterValue,
  ...patchCascaderFilterEntries(filterValue, defaultFilterValue),
});

/**
 * 渲染 Filter 前补齐 Cascader / PlatformCascader 的 value（须为 plain object）。
 * 所有非 Hour segment 均会调用：My Filter 回填、URL 恢复等路径可能写入 undefined/null。
 */
export const normalizeFilterValueForRender = (
  filterValue: Record<string, any>,
  defaultFilterValue: Record<string, any>,
): Record<string, any> => ({
  ...defaultFilterValue,
  ...filterValue,
  ...patchCascaderFilterEntries(filterValue, defaultFilterValue),
});

export const getKpiTrendsHourShortcuts = () =>
  createHourShortcuts(MAX_HOUR_RANGE_DAYS);

/** RangeHourPicker customOption.hour：disabledDate / changeClose / shortcuts */
export const getKpiTrendsHourRangePickerOption = () => ({
  disabledDate: createDefaultDisabledDate(MAX_HOUR_RANGE_DAYS),
  changeClose: false,
  shortcuts: getKpiTrendsHourShortcuts(),
});

/** filterConfirm 前校验 Hour 范围（超 7 天则裁切并回写 filterValue） */
export const clampHourRangeInFilterPayload = (
  payload: Record<string, any>,
  rangeTimeKey: string,
): Record<string, any> => {
  const rangeTimeType = payload?.[rangeTimeKey]?.type;
  if (!isKpiTrendsHourlyDateType(rangeTimeType)) return payload;

  const [start, end] = payload?.[rangeTimeKey]?.value || [];
  if (!moment.isMoment(start) || !moment.isMoment(end)) return payload;
  if (!start.isValid() || !end.isValid()) return payload;

  const [clampedStart, clampedEnd] = clampKpiTrendsHourlyRange(start, end);
  if (clampedStart.isSame(start) && clampedEnd.isSame(end)) return payload;

  return {
    ...payload,
    [rangeTimeKey]: {
      ...payload[rangeTimeKey],
      value: [clampedStart, clampedEnd],
    },
  };
};

/** KPI Trends DEFAULTINPUTLIST 传给 FilterList，替代全局硬编码 clamp */
export const normalizeKpiTrendsHourRangeValue = (
  type: string,
  range: [unknown, unknown],
): [unknown, unknown] => {
  if (type !== 'hour') return range;
  return normalizeHourRangeValue(range as any, MAX_HOUR_RANGE_DAYS);
};

export interface ISyncFilterRangeFromResponseParams {
  filterValueData: Record<string, any>;
  dateType: string;
  startDate: string;
  endDate: string;
}

/** 接口返回后回填 rangeTime；Hour 额外锁定 metric_selection */
export const syncFilterRangeFromResponse = ({
  filterValueData,
  dateType,
  startDate,
  endDate,
}: ISyncFilterRangeFromResponseParams): Record<string, any> => {
  const rangeValue = isKpiTrendsHourlyGranularity(dateType)
    ? parseResponseHourRange(startDate, endDate)
    : [startDate, endDate];

  const rangeTimeKey = filterKeyMap.RANGE_TIME;
  const metricSelectKey = filterKeyMap.METRIC_SELECT_FOR_KPI_TREND;

  const next = {
    ...filterValueData,
    [rangeTimeKey]: {
      ...filterValueData?.[rangeTimeKey],
      value: rangeValue,
    },
  };

  if (!isKpiTrendsHourlyGranularity(dateType)) {
    return next;
  }

  return {
    ...next,
    [metricSelectKey]: {
      value: buildHourlyMetricSelectionValue(),
    },
  };
};

/**
 * Hour 趋势图 x 轴展示补丁（在 useLineCharts 产出 option 后调用，不侵入共用 hook）。
 * category 仍为完整 YYYY-MM-DD HH:mm；仅 formatter 控制展示。
 */
export const applyKpiTrendsHourlyLineXAxis = (
  option: Record<string, any>,
  lineSeriesData: Record<string, any>,
  lineMetricName: string,
): Record<string, any> => {
  const times: string[] = [
    ...new Set<string>(
      (lineSeriesData?.[lineMetricName] || []).flatMap((item: any) =>
        (item?.history || [])
          .map((h: any) => h?.time as string | undefined)
          .filter((t: string | undefined): t is string => Boolean(t)),
      ),
    ),
  ];
  const xAxisData = getSortedDates(times, 'asc');
  if (!xAxisData.length) return option;

  // 每日首个小时：静态刻度显示日期，hover 显示日期+时分
  const dateTickIndices = new Set<number>();
  const dayBoundaryHours = new Set<string>();
  const seenDays = new Set<string>();
  xAxisData.forEach((value, index) => {
    const parsed = moment(value);
    if (!parsed.isValid()) return;
    const day = parsed.format('YYYY-MM-DD');
    if (seenDays.has(day)) return;
    seenDays.add(day);
    dateTickIndices.add(index);
    dayBoundaryHours.add(value);
  });

  const isSameDay = seenDays.size === 1;
  const totalPoints = xAxisData.length;
  const showDateTick = (index: number) => {
    if (!isSameDay) return dateTickIndices.has(index);
    if (index === 0 || index === totalPoints - 1) return true;
    const parsed = moment(xAxisData[index]);
    if (!parsed.isValid()) return false;
    if (parsed.hour() === 0 && parsed.minute() === 0) return false;
    return parsed.hour() % 2 === 0 && parsed.minute() === 0;
  };

  const formatPointerLabel = (rawValue: string) => {
    const parsed = moment(rawValue);
    if (!parsed.isValid()) return String(rawValue);
    if (isSameDay) {
      const index = xAxisData.indexOf(rawValue);
      return index === 0
        ? parsed.format('YYYY-MM-DD HH:mm')
        : parsed.format('HH:mm');
    }
    return dayBoundaryHours.has(rawValue)
      ? parsed.format('YYYY-MM-DD HH:mm')
      : parsed.format('HH:mm');
  };

  return {
    ...option,
    tooltip: {
      ...option.tooltip,
      axisPointer: {
        ...option.tooltip?.axisPointer,
        label: {
          ...option.tooltip?.axisPointer?.label,
          margin: option.xAxis?.axisLabel?.margin ?? 10,
          padding: [0, 0, 0, 0],
          formatter: (params: any) => formatPointerLabel(params?.value ?? ''),
        },
      },
    },
    xAxis: {
      ...option.xAxis,
      data: xAxisData,
      axisTick: { ...option.xAxis?.axisTick, interval: showDateTick },
      axisLabel: {
        ...option.xAxis?.axisLabel,
        interval: showDateTick,
        formatter: (value: string, index: number) => {
          if (isSameDay) {
            const parsed = moment(value);
            if (!parsed.isValid()) return value;
            if (index === 0) return parsed.format('YYYY-MM-DD');
            if (index === totalPoints - 1 && parsed.hour() >= 23) return '24:00';
            return parsed.format('HH:mm');
          }
          const parsed = moment(value);
          return parsed.isValid() ? parsed.format('YYYY-MM-DD') : value;
        },
      },
    },
  };
};
